import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingServiceAPI, Booking } from '../services/bookingServiceAPI';
import { paymentService } from '../services/paymentService';
import { configService } from '../services/ConfigService';
import RiyalSymbol from '../components/RiyalSymbol';
import '../styles/AdditionalPayment.css';

function AdditionalPayment() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const ticketCount = 1; // ثابت - تذكرة واحدة فقط
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeData = async () => {
      // تحديث الإعدادات من قاعدة البيانات
      try {
        await configService.reloadConfig();
      } catch (error) {
        console.error('Failed to reload config:', error);
      }
      
      if (bookingId) {
        loadBooking();
      }
    };
    
    initializeData();
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      // Check if bookingId is ObjectId or bookingCode
      const { booking } = await bookingServiceAPI.getBooking(bookingId!);
      if (booking) {
        setBooking(booking);
        // Don't pre-fill email - additional tickets are for new customers
        setEmail('');
      } else {
        setError('الحجز غير موجود');
      }
    } catch (err) {
      console.error('Error loading booking:', err);
      setError('الحجز غير موجود');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value && !validateEmail(value)) {
      setEmailError('يرجى إدخال عنوان بريد إلكتروني صحيح');
    } else {
      setEmailError('');
    }
  };

  const handlePurchase = async () => {
    if (!booking) return;
    
    // التحقق من الإيميل
    if (!email.trim()) {
      setEmailError('يرجى إدخال عنوان البريد الإلكتروني');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('يرجى إدخال عنوان بريد إلكتروني صحيح');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    setEmailError('');
    
    try {
      const bookingIdToUse = booking._id || booking.id || '';
      
      // إنشاء الدفع عبر Geidea للتذاكر الإضافية
      console.log('🔄 Starting Geidea payment for additional tickets...');
      const geideaResult = await paymentService.createAdditionalTicketsPayment(
        bookingIdToUse,
        ticketCount,
        email
      );
      
      if (geideaResult.success && geideaResult.paymentUrl) {
        window.location.href = geideaResult.paymentUrl;
      } else {
        throw new Error(geideaResult.message || 'فشل إنشاء عملية الدفع عبر Geidea');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'حدث خطأ في عملية الشراء');
      setIsProcessing(false);
    }
  };

  if (error && !booking) {
    return (
      <div className="additional-payment-page error-state">
        <div className="error-container">
          <h2>{error}</h2>
          <button onClick={() => navigate('/')} className="home-btn">
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="additional-payment-page loading">
        <div className="loader"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="additional-payment-page error-state">
        <div className="error-container">
          <h2>الحجز غير موجود</h2>
          <button onClick={() => navigate('/')} className="home-btn">
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  // الحصول على سعر التذكرة من الإعدادات
  const config = configService.getConfig();
  const ticketPrice = config.ticketSystem.ticketPrice;
  const totalAmount = ticketCount * ticketPrice;

  return (
    <motion.div 
      className="additional-payment-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="payment-container">
        <div className="payment-header">
          <h1>شراء تذاكر إضافية</h1>
          <p className="booking-info">
            رقم الحجز: <strong>#{booking.bookingCode}</strong>
          </p>
          <p className="customer-info">
            العميل: <strong>{booking.customerName}</strong>
          </p>
        </div>

        <div className="ticket-selection">
          <h3>🎫 احصل على تذكرة دخول الفوتوبوث!</h3>
          <div className="ticket-info-box">
            <div className="ticket-icon">📸</div>
            <p className="ticket-description">
              تذكرة دخول تخولك بالدخول والتصوير في الفوتوبوث
            </p>
            <p className="single-ticket-notice">
              📋 تذكرة دخول للفوتوبوث
            </p>
            <div className="price-highlight">
              <span className="price-label">السعر:</span>
              <span className="ticket-price">
                <strong>{ticketPrice} <RiyalSymbol size={16} /></strong>
              </span>
            </div>
          </div>
        </div>

        <div className="email-section">
          <label htmlFor="email">البريد الإلكتروني *</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="أدخل عنوان البريد الإلكتروني"
            className={emailError ? 'error' : ''}
          />
          {emailError && <div className="email-error">{emailError}</div>}
          <p className="email-info">سيتم إرسال التذكرة إلى هذا البريد الإلكتروني</p>
        </div>

        <div className="payment-method-section">
          <h3>طريقة الدفع</h3>
          <div className="payment-method-options">
            <div className="payment-method-option selected">
              <div className="payment-method-content">
                <span className="payment-method-name">بوابة الدفع الآمنة</span>
                <span className="payment-method-desc">بطاقة مدى فقط</span>
              </div>
            </div>
          </div>
        </div>


        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="payment-actions">
          <button 
            className="purchase-btn"
            onClick={handlePurchase}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="processing">
                <span className="spinner"></span>
                جاري المعالجة...
              </span>
            ) : (
              `ادفع ${totalAmount} ريال`
            )}
          </button>
          
          <button
            className="cancel-btn"
            onClick={() => navigate(-1)}
            disabled={isProcessing}
          >
            رجوع
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default AdditionalPayment;